import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import HardSkillProp from '../../../challenges/propTypes/hardSkillProp';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Select from '../../../../commonComponents/forms/inputs/Select';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import CourseItem from './CourseItem';
import HardSkillItem from './HardSkillItem';
import ServerSideCarousel from '../../../../commonComponents/carousel/ServerSideCarousel';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';
import {
  checkIfHasErrors,
  getErrors,
  getValidationRules,
} from '../../helpers/helper';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';

function stateFromProps(member = {}) {
  return member.hardSkills || [];
}

/**
 * Form to update the current logged user
 */
function HardSkills({ t, member, onMemberChange, hardSkills, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const validationRules = useMemo(
    () =>
      getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.HARD_SKILLS, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );
  const emptyCourseList = [
    {
      _id: null,
      name: t('profile_hardSkills_noCourses'),
      description: t('profile_hardSkills_addSkills'),
    },
  ];

  const [toAdd, setToAdd] = useState('');
  const [selectedHS, setSelectedHS] = useState(stateFromProps(member));
  const [errors, setErrors] = useState({
    hardSkills: false,
  });
  useEffect(() => setSelectedHS(stateFromProps(member)), [member]);

  console.log('*HardSkills', validationRules);

  const handleSubmit = useCallback(
    async evt => {
      evt.preventDefault();
      if (validationRules?.hardSkills?.required && selectedHS?.length === 0) {
        setErrors(prevErrors => ({
          ...prevErrors,
          hardSkills: true,
        }));
        return;
      }
      console.log('*HEY');
      const params = {
        hardSkills: selectedHS.map(hs => ({ ...hs, value: true })),
      };
      onMemberChange(params);
    },
    [onMemberChange, selectedHS],
  );

  const handleRemove = useCallback(
    hsId => setSelectedHS(prevState => prevState.filter(hs => hs._id !== hsId)),
    [],
  );

  const handleAdd = useCallback(() => {
    const skill = hardSkills.find(hs => hs._id === toAdd);
    if (skill) {
      setErrors(prevErrors => ({
        ...prevErrors,
        hardSkills: false,
      }));
      setSelectedHS(prevState => [skill].concat([...prevState]));
    }
    setToAdd('');
  }, [toAdd]);

  const handleSelectToAdd = useCallback(hsId => setToAdd(hsId), []);

  const selectedIds = selectedHS.map(hs => hs._id);
  const selectableHS = hardSkills.filter(hs => !selectedIds.includes(hs._id));

  const carouselReset = useRef(null);
  useEffect(() => {
    if (carouselReset.current) {
      carouselReset.current.reset();
    }
  }, [member]);

  const title = validationRules?.hardSkills?.required
    ? `${t('profile_tab_hardSkills')}*`
    : t('profile_tab_hardSkills');

  return (
    <form className="m-form m-form--fit m-form--label-align-right">
      <div className="m-portlet__body">
        <div className="row profile-skipStep">
          <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
            <FormSubtitle title={t('profile_hardSkills_eLearning')} />
          </div>
          {onSkipStep && (
            <div className="col-6 skip-wrapper btn-wrapper">
              <SkipStepButton onClick={onSkipStep} />
            </div>
          )}
        </div>
        <div className="hard-skill-course-container">
          <ServerSideCarousel
            ref={carouselReset}
            id="course-suggestion-carousel"
            fetchUrl="/profile/course-suggestions"
            defaultFetchError="error_fetchCourseSuggestions"
          >
            {courses => {
              const data = courses.length > 0 ? courses : emptyCourseList;
              return data.map(c => <CourseItem key={c._id} course={c} />);
            }}
          </ServerSideCarousel>
        </div>
        <FormSubtitle title={title} />
        <div className="col-lg-8 offset-lg-2 col-12 hard-skill-container">
          <div className="input-container">
            <Select
              id="addSkillSelector"
              className="add-skill-selector"
              placeholder={t('profile_hardSkills_selectorPlaceholder')}
              items={selectableHS.map(hs => ({ id: hs._id, text: hs.name }))}
              value={toAdd}
              onChange={handleSelectToAdd}
            />
            <button
              disabled={!toAdd}
              type="button"
              onClick={handleAdd}
              className="btn btn-dynamic m-btn m-btn--custom add-skill-button"
            >
              {t('profile_hardSkills_addSkill')}
            </button>
          </div>
          {errors?.hardSkills && (
            <ErrorMessage styleMessage={{ marginLeft: '-75px' }} />
          )}
          <TransitionGroup>
            {selectedHS.map(hs => (
              <CSSTransition
                key={hs._id}
                timeout={200}
                classNames="hard-skill-item"
              >
                <HardSkillItem hardSkill={hs} onRemove={handleRemove} />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </div>
      <FormFooter submitHandler={handleSubmit} />
    </form>
  );
}

HardSkills.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  hardSkills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType))
    .isRequired,
  onSkipStep: PropTypes.func,
};

HardSkills.defaultProps = {
  onSkipStep: null,
};

export default translate(HardSkills);
